import { Button, Modal, TextButton } from '@safetyhub/ui';
import { useLocation } from 'react-router-dom';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { MODAL_LAYER } from '../modalLayers';

/** 설치 완료 모달은 닫기 수단을 전부 끈다 — base가 요구하는 `onClose` 자리만 채운다. */
const NOOP = () => {};

// 하단 네비게이션 바가 없는 경로 (로그인, 공개 페이지 등)
const NO_NAVBAR_PREFIXES = [
  '/login',
  '/forgot-password',
  '/password-reset',
  '/signup',
  '/approval/',
  '/share/',
  '/tbm/sign/',
  '/tbm/educator/',
  '/educations/tbm/sign/',
  '/educations/tbm/educator/',
  '/delete-approval/',
  '/invite/',
  '/release-notes',
  '/auth/impersonate',
];

function hasNavbar(pathname: string): boolean {
  return !NO_NAVBAR_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function InstallBanner() {
  const layout = useDeviceLayout();
  const {
    showBanner,
    showIOSGuide,
    showIOSReminder,
    install,
    dismiss,
    dismissIOSGuide,
    reshowIOSGuide,
    isJustInstalled,
  } = useInstallPrompt();
  const location = useLocation();

  // PC에서는 미표시
  if (layout === 'desktop') return null;

  const slideUpStyle = (
    <style>{`
      @keyframes slideUp {
        from { transform: translateY(100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `}</style>
  );

  // 설치 완료 모달 (Android)
  if (isJustInstalled) {
    return (
      <>
        {slideUpStyle}
        {/*
          닫는 길은 「확인」(탭 닫기 유도) 하나다 — 종전에도 Esc·바깥 클릭·✕가 없었고, 설치를 마친
          사용자가 이 탭에 머물지 않게 하려는 화면이라 그 셋을 그대로 끈다. base가 주는 것은
          dialog 역할·포커스 트랩·스크롤 잠금이다. 층은 종전 `z-[100]`(최상위) 그대로다.
        */}
        <Modal
          open
          onClose={NOOP}
          ariaLabel="SafetyHub 설치 완료"
          size="sm"
          overlayZClassName={MODAL_LAYER.sub3}
          closeOnEsc={false}
          closeOnOverlay={false}
          showCloseButton={false}
        >
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 bg-brand-600 rounded-2xl flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div>
              <p className="text-lg font-semibold text-ink-900 mb-1">SafetyHub가 설치되었습니다!</p>
              <p className="text-sm text-ink-600">이 탭을 닫고 홈 화면에서 SafetyHub 아이콘을 탭해주세요</p>
            </div>
            <Button
              variant="primary"
              size="lg"
              onClick={() => { window.location.href = 'about:blank'; }}
              className="w-full"
            >
              확인
            </Button>
          </div>
        </Modal>
      </>
    );
  }

  if (!showBanner) return null;

  const withNavbar = hasNavbar(location.pathname);

  // iOS 리마인더 배너
  if (showIOSReminder) {
    return (
      <>
        {slideUpStyle}
        <div
          className="fixed left-0 right-0 z-[60]"
          style={{
            bottom: withNavbar ? 'calc(4rem + env(safe-area-inset-bottom, 0px))' : '0',
            animation: 'slideUp 0.3s ease-out',
          }}
        >
          <div className="bg-white border-t border-ink-200 shadow-lg px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-brand-600 rounded-control flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-ink-800">
                  이미 설치하셨나요? 이 탭을 닫고 홈 화면에서 SafetyHub를 열어주세요
                </p>
              </div>
              <div className="flex-shrink-0 flex flex-col gap-1 items-end">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => { window.location.href = 'about:blank'; }}
                >
                  확인
                </Button>
                <TextButton tone="neutral" onClick={reshowIOSGuide} className="px-3 py-1.5 text-xs">
                  아직 설치 안 했어요
                </TextButton>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {slideUpStyle}
      <div
        className="fixed left-0 right-0 z-[60]"
        style={{
          bottom: withNavbar ? 'calc(4rem + env(safe-area-inset-bottom, 0px))' : '0',
          animation: 'slideUp 0.3s ease-out',
        }}
      >
        <div className="bg-white border-t border-ink-200 shadow-lg px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-brand-600 rounded-control flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              {showIOSGuide ? (
                <p className="text-sm text-ink-800">
                  공유 버튼 → '홈 화면에 추가'를 탭한 후, 홈 화면에서 SafetyHub를 열어주세요
                </p>
              ) : (
                <p className="text-sm text-ink-800">SafetyHub 앱을 설치하시겠습니까?</p>
              )}
            </div>
            {showIOSGuide ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={dismissIOSGuide}
                className="flex-shrink-0 border-brand-600 text-brand-600 hover:text-brand-700"
              >
                확인
              </Button>
            ) : (
              <div className="flex-shrink-0 flex gap-2">
                {/* 하드코딩 파랑(#2563eb)이던 자리 — 브랜드 primary로 통일. */}
                <Button variant="primary" size="sm" onClick={install}>
                  설치
                </Button>
                <Button variant="secondary" size="sm" onClick={dismiss}>
                  나중에
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
